import React, { useCallback, useEffect, useState } from 'react';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { Box, Button } from '@mui/material';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { EntityField, Operator, SelectedEntityFields } from '../../../../models/query-builder';
import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import Filter from './filter';
import SaveQueryModal from './save-query-modal';
import * as Yup from 'yup';
import { useFormContext } from 'react-hook-form';
import { CONDITION_OPERATORS } from './conditinal-operator';
import { FormSelect } from '../../hooks-form-querybuilder/form-select.component';
import DeleteIcon from '@mui/icons-material/Delete';
import { Add } from '@mui/icons-material';
import useEnhancedEffect from '@mui/material/utils/useEnhancedEffect';
import ConfirmDialog from '../../../../components/confirm-dialog';

interface IFiltersPage {
  fields: EntityField[];
  operators: Operator[];

  index: number;
  groupKeysCount: number;
  handleDeleteGroup: (data: any) => void;
  groupKey: string;
  addNewGroupKey: () => void;
  prevGroupKey: string;
  defaultFilterKeys: string[] | undefined;
  defaultDependencyIds?: {
    [key: string]: string;
  } | null;
}

interface FilterData {
  fieldId: number;
  operatorId: number;
  fieldValue: string;
  joinCondition?: string;
}

const FiltersContainer = ({
  fields,
  operators,
  defaultFilterKeys,
  index,
  groupKeysCount,
  groupKey,
  handleDeleteGroup,
  addNewGroupKey,
  defaultDependencyIds,
}: IFiltersPage) => {
  const {
    setValue,
    unregister,
    watch,
    formState: { errors },
  } = useFormContext();
  const [keys, setKeys] = useState([nanoid(32)]);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  useEffect(() => {
    if (defaultFilterKeys) {
      setKeys(defaultFilterKeys);
    }
  }, [defaultFilterKeys]);

  const [dependencyIds, setDependencyIds] = useState<{
    [key: string]: string;
  } | null>(null);

  useEffect(() => {
    if (defaultDependencyIds) {
      setDependencyIds(defaultDependencyIds);
    }
  }, [defaultDependencyIds]);

  // Update this when a key is added
  const addNewKey = () => {
    const newKey = nanoid(32);
    setKeys((prevKeys) => [...prevKeys, newKey]);
  };

  const handleRemoveDependencyIds = (filterKey: string) => {
    const field = watch(`groups.${groupKey}.filters.${filterKey}`);
    if (!field?.fieldName) {
      return;
    }
    setDependencyIds((prev) => {
      const updatedDependencyIds = { ...prev };
      delete updatedDependencyIds[field.fieldName];
      return updatedDependencyIds;
    });
  };

  const handleDelete = useCallback(
    (filterKey: string) => {
      // Clearing last selected condition join
      const secondLastKey = keys[keys.length - 2];
      if (secondLastKey) {
        setValue(`filters.${[secondLastKey]}.joinCondition`, '');
      }

      setKeys((prevKeys) => prevKeys.filter((key) => key !== filterKey));
      handleRemoveDependencyIds(filterKey);
    },
    [keys]
  );

  const showAddButton = groupKeysCount === index + 1;
  const showDeleteButton = groupKeysCount > 1;

  const onDeleteGroup = useCallback(() => {
    handleDeleteGroup(groupKey);
  }, [handleDeleteGroup]);

  useEffect(() => {
    return () => {
      unregister(`groups.${groupKey}`);
    };
  }, []);

  const isFirstItem = index === 0;

  const handleDependencyIds = (fieldName: string, ids: string) => {
    setDependencyIds((prevDepIds) => ({ ...prevDepIds, [fieldName]: ids }));
  };

  return (
    <div className="qury-grid-wrap-outer queryWrapContainer">
      {!isFirstItem && (
        <div
          style={{
            padding: '15px 15px 0px 10px',
            marginBottom: '5px',
            width: 250,
          }}
        >
          <FormSelect
            name={`groups.${groupKey}.joinCondition`}
            options={CONDITION_OPERATORS}
            label="Condition Operator"
            required={!watch(`groups.${groupKey}.joinCondition`)}
          />
        </div>
      )}
      <Box className="qury-grid-wrap">
        {keys.map((key, index) => (
          <Filter
            key={key}
            filterKey={key}
            groupKey={groupKey}
            fields={fields}
            operators={operators}
            addNewKey={addNewKey}
            keysCount={keys.length}
            handleDelete={handleDelete}
            index={index}
            dependencyIds={dependencyIds}
            handleDependencyIds={handleDependencyIds}
          />
        ))}
        <div
          style={{
            display: 'flex',
            gap: '15px',
            justifyContent: 'end',
            position: 'relative',
            right: '15px',
            bottom: '13px',
          }}
        >
          {showDeleteButton && (
            <Button
              size="small"
              variant="contained"
              onClick={() => setIsDelete(true)}
              color="error"
              sx={{ backgroundColor: '#FE7F7F' }}
            >
              <DeleteIcon /> Delete
            </Button>
          )}
          {showAddButton && (
            <Button variant="contained" onClick={addNewGroupKey} size="small">
              <Add /> Add Clause
            </Button>
          )}
        </div>
      </Box>
      {isDelete && (
        <ConfirmDialog
          message="Are you sure that you want to delete?"
          onClose={() => setIsDelete(false)}
          onConfirm={() => onDeleteGroup()}
          open={isDelete}
          titleForConfirmButton="Delete"
        />
      )}
    </div>
  );
};

export default FiltersContainer;
